import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, ChevronRight, ShieldCheck, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatAgo } from '@/lib/format';
import { useAnomalies } from '@/api/hooks/anomalies';
import type { Anomaly } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { AckDialog, SEVERITY, anomalyLink } from '@/components/anomalies/AnomalyParts';

/** Колко сигнала се виждат, преди „Всички в Аномалии". */
const SHOWN = 5;

/**
 * СИГНАЛИТЕ ЗА ТОЗИ БУС — аномалиите от всички модули, които са за него:
 * километри срещу GPS, гориво, документи по заявките му, сервиз, винетки.
 * Същите данни като менюто „Аномалии" (едно зареждане за двете), само
 * филтрирани — „Прегледано" тук го маха и оттам.
 */
export function VanSignalsCard({ vehicleId }: { vehicleId: string }) {
  const anomalies = useAnomalies(false);
  const [acking, setAcking] = useState<Anomaly | null>(null);
  if (!anomalies.data) return null;
  const mine = anomalies.data.items.filter((a) => a.vehicleId === vehicleId);
  const high = mine.filter((a) => a.severity === 'high').length;

  if (mine.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-ok/30 bg-ok-soft/30 px-4 py-2.5 text-sm text-ok">
        <ShieldCheck className="size-4" />
        Няма сигнали за този бус.
      </div>
    );
  }

  return (
    <Card className={cn('overflow-hidden', high > 0 && 'border-danger/40')}>
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <TriangleAlert className={cn('size-4', high > 0 ? 'text-danger' : 'text-warn')} />
            Сигнали за буса
          </span>
        }
        subtitle={`${mine.length} отворени${high ? ` · ${high} важни` : ''}`}
        actions={
          mine.length > SHOWN ? (
            <Link to="/anomalies" className="inline-flex items-center gap-0.5 text-xs text-accent hover:underline">
              Всички в „Аномалии“ <ChevronRight className="size-3.5" />
            </Link>
          ) : null
        }
      />
      <CardBody className="p-0">
        <ul className="divide-y divide-line">
          {mine.slice(0, SHOWN).map((a) => (
            <li key={a.key} className="flex items-start gap-3 px-4 py-2.5">
              <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', SEVERITY[a.severity].dot)} title={SEVERITY[a.severity].label} />
              <div className="min-w-0 flex-1">
                <div className="text-sm">{a.title}</div>
                {a.detail ? <div className="text-xs text-ink-dim">{a.detail}</div> : null}
                <div className="mt-0.5 text-[11px] text-ink-faint">
                  {anomalies.data.categories[a.category]} · {formatAgo(a.at)}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {a.link && !a.link.startsWith(`/vehicles/${vehicleId}`) ? (
                  <Link {...anomalyLink(a.link)} className="inline-flex items-center gap-0.5 rounded px-2 py-1 text-xs text-accent hover:bg-raised">
                    Отвори <ChevronRight className="size-3.5" />
                  </Link>
                ) : null}
                <Button size="sm" variant="secondary" icon={<Check className="size-3.5" />} onClick={() => setAcking(a)}>
                  Прегледано
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </CardBody>
      <AckDialog anomaly={acking} onClose={() => setAcking(null)} />
    </Card>
  );
}
